import { jwtDecode } from "jwt-decode";
import { persistor } from "../store";
import { clearAuth } from "../store/features/auth/authSlice";
import {
  pickManagedClubIdFromJwtPayload,
  pickManagedClubIdFromUser,
} from "./managedClubId";
import {
  isLikelyJwt,
  pickAccessTokenFromLoginPayload,
  pickRefreshTokenFromLoginPayload,
  readStoredAccessToken,
  stripBearerPrefix,
} from "./authToken";
import Constant from "../util/constant";

/** Normalize admin login API body (`{ message, data }` or flat tokens). */
export function normalizeAdminLoginPayload(payload) {
  const root = payload && typeof payload === "object" ? payload : {};
  const data =
    root.data && typeof root.data === "object" ? root.data : root;
  const user = data.user ?? root.user ?? null;
  const accessToken = pickAccessTokenFromLoginPayload(payload);
  const refreshToken = pickRefreshTokenFromLoginPayload(payload);
  const role = String(data.role ?? user?.role ?? root.role ?? "").trim();
  return {
    message: root.message ?? "",
    user,
    accessToken,
    refreshToken,
    role,
  };
}

export function resolveRoleFromAccessToken(accessToken) {
  const token = stripBearerPrefix(accessToken);
  if (!isLikelyJwt(token)) return "";
  try {
    const decoded = jwtDecode(token);
    return String(decoded?.role ?? "").trim();
  } catch {
    return "";
  }
}

export function resolveLoginRole(normalized) {
  return (
    normalized.role ||
    resolveRoleFromAccessToken(normalized.accessToken) ||
    ""
  );
}

export function getPostLoginPath(role) {
  switch (role) {
    case Constant.ROLES.ADMIN:
      return "/club-sales-analysis?section=sales";
    case Constant.ROLES.ORGANISER:
      return "/events-list";
    case Constant.ROLES.CLUB_MANAGER:
      return "/club-sales-analysis?section=sales";
    case Constant.ROLES.CLUB_EMPLOYEE:
      return "/club-offline-sale";
    default:
      return null;
  }
}

export const CLUB_STAFF_LAST_PATH_KEY = "clubStaffLastPath";

const CLUB_STAFF_ALLOWED_PATH_PREFIXES = [
  "/club-sales-analysis",
  "/club-offline-sale",
  "/club-guestlist",
  "/club-website",
  "/club-catalog",
  "/club-employees",
  "/club-cashiers",
  "/club-table-booking",
  "/change-password",
  "/events-list",
];

const isClubStaffAllowedPath = (pathname) => {
  const p = String(pathname || "").trim();
  if (!p) return false;
  if (
    CLUB_STAFF_ALLOWED_PATH_PREFIXES.some(
      (prefix) => p === prefix || p.startsWith(`${prefix}/`)
    )
  ) {
    return true;
  }
  return /\/clubs\/[^/]+\/table-booking(?:\/|$)/.test(p);
};

/** Remember last club-staff screen so refresh/login does not always land on POS. */
export function rememberClubStaffPath(pathname) {
  const p = String(pathname || "").trim();
  if (!p || p === "/") return;
  if (isClubStaffAllowedPath(p)) {
    window.sessionStorage.setItem(CLUB_STAFF_LAST_PATH_KEY, p);
  }
}

/** After login or hitting `/` while session is valid, resume last staff page when possible. */
export function resolveClubStaffResumePath(role) {
  const last = window.sessionStorage.getItem(CLUB_STAFF_LAST_PATH_KEY);
  if (last && last.startsWith("/") && last !== "/") {
    if (isClubStaffAllowedPath(last)) {
      if (role === Constant.ROLES.CLUB_EMPLOYEE) {
        const managerOnly =
          last.startsWith("/club-website") ||
          last.startsWith("/club-catalog") ||
          last.startsWith("/club-employees") ||
          last.startsWith("/club-cashiers") ||
          last.startsWith("/club-guestlist") ||
          last.startsWith("/club-table-booking") ||
          last.startsWith("/events-list") ||
          /\/clubs\/[^/]+\/table-booking(?:\/|$)/.test(last);
        if (!managerOnly) return last;
      } else {
        return last;
      }
    }
  }
  return getPostLoginPath(role);
}

export function hasValidAuthSession() {
  const token = readStoredAccessToken();
  const role = window.localStorage.getItem("role");
  return Boolean(
    token &&
    isLikelyJwt(token) &&
    role &&
    role !== "undefined"
  );
}

/**
 * Persist tokens, role, and optional remember-me credentials after login.
 */
export function persistAuthSession({
  accessToken,
  refreshToken,
  user,
  role,
  rememberMe,
  email,
  password,
}) {
  if (accessToken) {
    const normalized = stripBearerPrefix(accessToken);
    if (isLikelyJwt(normalized)) {
      window.localStorage.setItem(
        Constant.LOCALSTORAGEKEYS.ACCESSTOKEN,
        normalized
      );
    } else {
      window.localStorage.removeItem(Constant.LOCALSTORAGEKEYS.ACCESSTOKEN);
    }
  }
  if (refreshToken) {
    const normalizedRefresh = stripBearerPrefix(refreshToken);
    if (normalizedRefresh) {
      window.localStorage.setItem(
        Constant.LOCALSTORAGEKEYS.REFRESHTOKEN,
        normalizedRefresh
      );
    }
  }
  if (user?.fullname) {
    window.localStorage.setItem(
      Constant.SESSIONSTORAGEKEYS.USER_NAME,
      user.fullname
    );
  }
  if (user?.image) {
    window.localStorage.setItem(
      Constant.SESSIONSTORAGEKEYS.USER_IMAGE,
      user.image
    );
  }
  if (user?._id) {
    window.localStorage.setItem(Constant.SESSIONSTORAGEKEYS.ID, user._id);
  }
  const empref = user?.empref ?? user?.empRef ?? user?.emp_ref;
  if (empref != null && String(empref).trim() !== "") {
    window.localStorage.setItem(
      Constant.LOCALSTORAGEKEYS.EMPREF,
      String(empref).trim().toUpperCase()
    );
  } else {
    window.localStorage.removeItem(Constant.LOCALSTORAGEKEYS.EMPREF);
  }
  if (role) {
    window.localStorage.setItem("role", role);
  }
  window.localStorage.setItem("login", "in");

  const isClubStaffRole =
    role === Constant.ROLES.CLUB_MANAGER ||
    role === Constant.ROLES.CLUB_EMPLOYEE;
  const clubForManager = pickManagedClubIdFromUser(user);
  let clubFromToken = null;
  try {
    if (accessToken) {
      const normalizedToken = stripBearerPrefix(accessToken);
      if (isLikelyJwt(normalizedToken)) {
        clubFromToken = pickManagedClubIdFromJwtPayload(
          jwtDecode(normalizedToken)
        );
      }
    }
  } catch {
    clubFromToken = null;
  }
  const clubToStore = clubForManager ?? clubFromToken;
  if (isClubStaffRole && clubToStore) {
    window.localStorage.setItem(
      Constant.LOCALSTORAGEKEYS.MANAGED_CLUB_ID,
      String(clubToStore)
    );
  } else {
    window.localStorage.removeItem(Constant.LOCALSTORAGEKEYS.MANAGED_CLUB_ID);
  }

  window.sessionStorage.setItem(
    Constant.SESSIONSTORAGEKEYS.REMEMBER_ME,
    rememberMe ? "true" : "false"
  );
  if (rememberMe) {
    window.sessionStorage.setItem(
      Constant.SESSIONSTORAGEKEYS.RIZZTIX_REMEMBER_ME_EMAIL,
      email ?? ""
    );
    window.sessionStorage.setItem(
      Constant.SESSIONSTORAGEKEYS.RIZZTIX_REMEMBER_ME_PASSWORD,
      password ?? ""
    );
  } else {
    window.sessionStorage.setItem(
      Constant.SESSIONSTORAGEKEYS.RIZZTIX_REMEMBER_ME_EMAIL,
      ""
    );
    window.sessionStorage.setItem(
      Constant.SESSIONSTORAGEKEYS.RIZZTIX_REMEMBER_ME_PASSWORD,
      ""
    );
  }
}

/** Full sign-out: storage, redux-persist, and in-memory auth slice. */
export async function clearAuthSession(dispatch) {
  if (dispatch) {
    dispatch(clearAuth());
  }
  window.localStorage.clear();
  window.sessionStorage.clear();
  try {
    await persistor.purge();
  } catch {
    /* ignore purge errors */
  }
}
